import React, {
  useState,
  createContext,
  useContext,
  ReactNode,
  useEffect,
} from 'react';

export interface LeaseableResponseDTO {
  readonly promptVersion?: string;
  readonly subtotal?: string;
  readonly salesTax?: string;
  readonly total?: string;
  readonly items?: AcimaResponseItem[];
}

export interface AcimaResponseItem {
  readonly category?: string;
  readonly classification?: string;
  readonly consumerProduct?: string;
  readonly description?: string;
  readonly details?: string;
  readonly leaseable?: number;
  readonly quantity?: number;
  readonly reason?: string;
  readonly price?: string;
  readonly originalPrice?: string;
  readonly savings?: string;
}

export interface ShopLocalRmsInfo {
  selectedDepartment: string;
  selectedSubDepartment: string;
  selectedBraket: string;
  selectedType: string;
  selectedSubType: string;
  rmsItemNumber: string;
}
export interface ShopLocalProductInfo {
  productName: string;
  quantity: string;
  unitCost: string;
  weeklyRate: string;
  cashPrice: string;
  totalCost: string;
  weeklyTerm: string;
  rmsInfo: ShopLocalRmsInfo;
  errorMsg: string;
  leasable: boolean;
}
export interface ShopLocalData {
  vendorName: string;
  estimatedWeeklyPayment: string;
  estimatedWeeklyTerm: string;
  totalCost: string;
  products: ShopLocalProductInfo[];
}

const emptyProduct: ShopLocalProductInfo = {
  productName: '',
  quantity: '',
  unitCost: '',
  errorMsg: '',
  rmsInfo: {} as ShopLocalRmsInfo,
  leasable: false,
  weeklyRate: '',
  weeklyTerm: '',
};

export interface ShopLocalMutators {
  setVendor: (vendorName: string) => void;
  setWeeklyPayment: (estimatedWeeklyPayment: string) => void;
  setWeeklyTerm: (estimatedWeeklyTerm: string) => void;
  setTotalCost: (totalCost: string) => void;
  setAllProducts: (products: ShopLocalProductInfo[]) => void;
  resetProducts: () => void;
}

const ShopLocalDataContext = createContext<ShopLocalData>({} as ShopLocalData);
const ShopLocalMutatorContext = createContext<ShopLocalMutators>(
  {} as ShopLocalMutators
);
export interface PropReturnType {
  children: ReactNode;
}

export const ShopLocalProvider = (props: PropReturnType) => {
  const [vendorName, setVendorName] = useState<string>('');
  const [estimatedWeeklyPayment, setEstimatedWeeklyPayment] =
    useState<string>('');
  const [estimatedWeeklyTerm, setEstimatedWeeklyTerm] = useState<string>('');
  const [totalCost, setTotalCost] = useState<string>('');
  const [products, setProducts] = useState<ShopLocalProductInfo[]>([
    emptyProduct,
  ]);

  const setVendor = (vendorName: string) => {
    setVendorName(vendorName);
  };
  const setWeeklyPayment = (estimatedWeeklyPayment: string) => {
    setEstimatedWeeklyPayment(estimatedWeeklyPayment);
  };
  const setWeeklyTerm = (estimatedWeeklyTerm: string) => {
    setEstimatedWeeklyTerm(estimatedWeeklyTerm);
  };

  const resetProducts = () => {
    setProducts([emptyProduct]);
    setVendorName('');
    setEstimatedWeeklyPayment('');
    setEstimatedWeeklyTerm('');
    setTotalCost('');
  };
  const setAllProducts = (products: ShopLocalProductInfo[]) => {
    setProducts(products);
  };

  useEffect(() => {
    resetProducts();
  }, []);

  return (
    <ShopLocalDataContext.Provider
      value={{
        vendorName,
        estimatedWeeklyPayment,
        estimatedWeeklyTerm,
        totalCost,
        products,
      }}
    >
      <ShopLocalMutatorContext.Provider
        value={{
          setVendor,
          setWeeklyPayment,
          setWeeklyTerm,
          setTotalCost,
          setAllProducts,
          resetProducts,
        }}
      >
        {props.children}
      </ShopLocalMutatorContext.Provider>
    </ShopLocalDataContext.Provider>
  );
};

export const useShopLocalData = () => useContext(ShopLocalDataContext);
export const useShopLocalActions = () => useContext(ShopLocalMutatorContext);
